import type { Project } from "@baton/api";
import type { Runtime } from "@baton/core";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { projectQuery, projectsQuery, workspaceQuery } from "../../app/queries.ts";

/**
 * 001 § Projects (`m0068`): the Team's own "General" project, which every task without a project of
 * its own is in. "No project" is not a state a task can be in, since every task is in a project: it
 * is this project, which the project list never lists (`GET /workspaces/{ws}/projects` excludes it)
 * and the workspace read names by `default_project_id`. Read by its id wherever a project is chosen,
 * so General is always offered there (2026-10-01, by the owner's report: "we can't move tasks back to
 * be in General ... If we select a project and we unselect it, how do we do that? I noticed that in
 * the new task thing it doesn't work").
 */
export function useGeneralProject(runtime: Runtime, ws: string, enabled = true): { readonly id: string | null; readonly project: Project | undefined; readonly pending: boolean } {
  const workspace = useQuery({ ...workspaceQuery(runtime, ws), enabled });
  const id = workspace.data?.default_project_id ?? null;
  const general = useQuery({ ...projectQuery(runtime, ws, id ?? ""), enabled: enabled && id !== null });
  return { id, project: general.data, pending: enabled && (workspace.isPending || (id !== null && general.isPending)) };
}

/**
 * The Team's projects as the Projects page and the sidebar list them (2026-10-03, by the owner's
 * approval of the product review: a task made with no project landed in a General project that no
 * list showed, so it had no visible home): General pinned first, then the project list in its order.
 * General keeps its own rules there, being the Team's default, which is never archived or deleted.
 */
export function withGeneral(projects: readonly Project[], general: Project | undefined): readonly Project[] {
  return general === undefined ? projects : [general, ...projects.filter((project) => project.id !== general.id)];
}

/**
 * The projects a composer on a list spanning projects writes to (the list's, the board's, the
 * calendar's and the timeline's inline composers): General first, then every live project in the
 * project list's order; none on one project's own collection, whose composer has no project chip.
 */
export function useComposerProjects(runtime: Runtime, ws: string, project: Project | null): readonly Project[] {
  const spanning = project === null;
  const list = useQuery({ ...projectsQuery(runtime, ws), enabled: spanning });
  const general = useGeneralProject(runtime, ws, spanning);
  return useMemo(() => {
    if (!spanning) return [];
    const live = (list.data?.items ?? []).filter((entry) => entry.archived_at === undefined || entry.archived_at === null);
    return general.project === undefined ? live : [general.project, ...live];
  }, [spanning, list.data, general.project]);
}
